import { ChatAttachmentWarning } from '@agent/agent-interface/contact-view/chat/model/chat-attachment-error';
import { ChatAttachmentInfo } from '@agent/agent-interface/contact-view/chat/model/chat-attachment-info';
import { ChatProgressInfo } from '@agent/agent-interface/contact-view/chat/model/chat-progress-info';
import { ChatSocketService, MESSAGE_ENDPOINT } from '@agent/agent-interface/contact-view/chat/services/chat-socket.service';
import { HttpEventType } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { ChannelUtil } from '@commons/util/helper/channel';
import { Store } from '@ngrx/store';
import { filter, Observable, Subject, takeUntil, withLatestFrom } from 'rxjs';

import { AgentSkillFacade, AppConf, HttpService, isAgentReady, TranslateService, waitToLoad } from '@contact/upgrade';

export const FILE_MAX_SIZE: number = 6000000;
export const GLOBAL_MAX_SIZE: number = 16000000;
export const MAX_ATTACHMENT_COUNT: number = 6;

@Injectable({
  providedIn: 'root',
})
export class ChatAttachmentService {
  private chatUrl: string | undefined;
  private attachmentMap = new Map<string, ChatAttachmentInfo>();
  extensions: Array<string>;

  private readonly _destroy$ = new Subject<void>();
  readonly destroy$ = this._destroy$.asObservable();

  constructor(
    private readonly appConf: AppConf,
    private readonly translateService: TranslateService,
    private readonly http: HttpService,
    private readonly injector: Injector,
    private readonly store$: Store,
    private readonly agentSkillFacade: AgentSkillFacade
  ) {
    this.appConf.pipe(takeUntil(this.destroy$)).subscribe(({ chat }) => {
      this.extensions = chat?.chatAttachmentExtension ?? [];
      this.chatUrl = chat?.chatBaseUrl;
    });
  }

  init() {
    // adding unload behavior for chat attachment
    this.store$
      .pipe(
        waitToLoad([isAgentReady]),
        withLatestFrom(this.agentSkillFacade.getAll(ChannelUtil.INCOMING_CHAT), (_, skills) => skills.length > 0),
        filter(hasChatPermission => hasChatPermission),
        takeUntil(this.destroy$)
      )
      .subscribe(() =>
        window.addEventListener('beforeunload', e => {
          if (!this.canLogout()) {
            const message = this.translateService.instant('agent:panel.chat.attachment.confirm_message');
            let ee = e || window.event;
            if (ee) {
              ee.returnValue = message;
            }
            return message;
          }
          return undefined;
        })
      );
  }

  destroy() {
    this._destroy$.next();
  }

  getAttachmentInfo(customerId: string): ChatAttachmentInfo | undefined {
    return this.attachmentMap.get(customerId) ? this.attachmentMap.get(customerId) : undefined;
  }

  removeInfo(customerId: string): void {
    const info = this.attachmentMap.get(customerId);
    if (info) {
      this.attachmentMap.delete(customerId);
    }
  }

  processFileList(files: File[], customerId: string): ChatAttachmentInfo {
    let info = this.attachmentMap.get(customerId);
    if (!info) {
      info = new ChatAttachmentInfo([], [], []);
      this.attachmentMap.set(customerId, info);
    }
    info.warnings = [];

    if (files.length + info.validatedFiles.length > MAX_ATTACHMENT_COUNT) {
      info.warnings.push(new ChatAttachmentWarning(['agent:panel.chat.attachment.max_attachment_count']));
      info.validatedFiles = [];
    } else {
      files.forEach(file => {
        const warnings = [];
        this.validateExtension(file, warnings);
        this.validateSize(file, info, warnings);
        if (warnings.length === 0 && !info!.validatedFiles.find(vf => vf.name === file.name)) {
          info!.validatedFiles.push(file);
        }
        info!.warnings = info!.warnings.concat(warnings);
      });
    }
    return info;
  }

  removeWarning(error: ChatAttachmentWarning, customerId: string) {
    const info = this.attachmentMap.get(customerId);
    if (info) {
      const index = info.warnings.indexOf(error);
      info.warnings.splice(index, 1);
    }
  }

  removeFile(file: File, customerId: string) {
    const info = this.attachmentMap.get(customerId);
    if (info) {
      const index = info.validatedFiles.indexOf(file);
      info.validatedFiles.splice(index, 1);
    }
  }

  sendFilesIfExist(customerId: string) {
    let info = this.attachmentMap.get(customerId);
    if (info) {
      info.validatedFiles.forEach(file => {
        const index = info!.progressResponse.push(new ChatProgressInfo(0, file, Date.now())) - 1;
        this.uploadAndUpdateProgress(file, customerId, info, index);
      });
      info.validatedFiles = [];
      info.warnings = [];
    }
  }

  downloadFile(uri: string): Observable<Blob> {
    return this.http.get(this.chatUrl + uri, {
      responseType: 'blob',
    });
  }

  downloadBlob(response: Blob, fileName: string) {
    // Chrome & FF
    const url = window.URL.createObjectURL(response);
    const a = document.createElement('a');
    a.download = fileName;
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  cancelUpload(attachment: ChatProgressInfo, customerId: string) {
    const info = this.attachmentMap.get(customerId);
    if (info) {
      const index = info.progressResponse.indexOf(attachment);
      const sub = info.progressResponse[index].uploadSubscription;
      if (sub) {
        sub.unsubscribe();
      }
      info.progressResponse.splice(index, 1);
      this.clearProgressIfNeeded(info);
    }
  }

  retryUpload(attachment: ChatProgressInfo, customerId: string) {
    const info = this.attachmentMap.get(customerId);
    if (info) {
      const index = info.progressResponse.indexOf(attachment);

      const sub = info.progressResponse[index].uploadSubscription;
      if (sub) {
        sub.unsubscribe();
      }
      info.progressResponse[index].errorMessages = undefined;

      this.uploadAndUpdateProgress(attachment.file, customerId, info, index);
    }
  }

  canLogout(): boolean {
    for (let info of Array.from(this.attachmentMap.values())) {
      if (info.progressResponse.length !== 0) {
        return false;
      }
    }
    return true;
  }

  private uploadAndUpdateProgress(file, customerId, info, index) {
    const formData = new FormData();
    formData.append('file', file);

    info.progressResponse[index].uploadSubscription = this.upload(formData, customerId).subscribe(
      event => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const percentDone = Math.round((100 * event.loaded) / event.total);
            info.progressResponse[index].progress = percentDone;
            break;

          case HttpEventType.Response:
            this.injector
              .get(ChatSocketService)
              .sendFileMessage(MESSAGE_ENDPOINT, event.body.fileDownloadUri, event.body.fileName, event.body.fileSize);
            info.progressResponse[index] = null;
            this.clearProgressIfNeeded(info);
            break;

          default:
            break;
        }
      },
      error => {
        info.progressResponse[index].errorMessages = this.prepareErrorMessages(error);
      }
    );
  }

  private prepareErrorMessages(error) {
    if (error.error instanceof ErrorEvent) {
      return [error.error.message];
    } else if (error.error instanceof Array) {
      return error.error.map(err => 'agent:panel.chat.attachment.' + err);
    } else {
      return ['agent:panel.chat.attachment.loading_error_message'];
    }
  }

  private upload(formData: FormData, customerId: string): Observable<any> {
    return this.http.post<any>(`${this.chatUrl + '/api/file/upload/' + customerId}`, formData, {
      observe: 'events',
      reportProgress: true,
    });
  }

  private clearProgressIfNeeded(info) {
    if (info.progressResponse.filter(res => res !== null).length === 0) {
      info.progressResponse = [];
    }
  }

  private validateExtension(file, warnings) {
    if (this.extensions && this.extensions.indexOf(this.getFileExtension(file.name)) <= -1) {
      warnings.push(
        new ChatAttachmentWarning(
          ['agent:panel.chat.attachment.not_authorized_file_part1', 'agent:panel.chat.attachment.not_authorized_file_part2'],
          file.name
        )
      );
    }
  }

  private validateSize(file, info, warnings) {
    const globale =
      info &&
      info.validatedFiles &&
      info.validatedFiles.length > 0 &&
      info.validatedFiles.map(file => file && file.size).reduce((prev, next) => prev + next);

    if (file.size > FILE_MAX_SIZE) {
      warnings.push(
        new ChatAttachmentWarning(
          ['agent:panel.chat.attachment.file_size_exceeded_part1', 'agent:panel.chat.attachment.file_size_exceeded_part2'],
          file.name
        )
      );
    }

    if (file.size + globale > GLOBAL_MAX_SIZE) {
      warnings.push(new ChatAttachmentWarning(['agent:panel.chat.attachment.global_size_exceeded']));
    }
  }

  private getFileExtension(filename) {
    return filename.match(/[^.\\/:*?"<>|\r\n]+$/g)[0];
  }
}
